import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUpRight, CalendarClock, History } from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { Section } from '@/components/Section'
import { EventCard } from '@/components/EventCard'
import { LocalizedLink } from '@/components/LocalizedLink'
import { usePageSEO } from '@/hooks/usePageSEO'
import type { SupportedLanguage } from '@/i18n'
import {
  eventKey,
  getUpcomingEvents,
  getPastEvents,
  type FenixEvent,
} from '@/data/events'
import { cn } from '@/lib/utils'

/* ============================================================
   Count pill — shared industrial chip pattern (mirrors NewsPage)
   ============================================================ */
function CountPill({ count, label }: { count: number; label: string }) {
  return (
    <div className="inline-flex items-center gap-3 self-start rounded-sm border border-ink-200 bg-white px-4 py-2 text-xs font-semibold tracking-[0.14em] text-ink-600 uppercase">
      <span aria-hidden="true" className="block h-[3px] w-5 bg-brand-500" />
      <span className="font-display font-bold tabular-nums text-base text-ink-900">
        {String(count).padStart(2, '0')}
      </span>
      {label}
    </div>
  )
}

/* ============================================================
   Events grid wrapper — pushes actual i18n lookup into the page
   so EventCard stays dumb.
   ============================================================ */
function EventsGrid({
  items,
  currentLang,
}: {
  items: FenixEvent[]
  currentLang: SupportedLanguage
}) {
  const { t } = useTranslation()
  void currentLang
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
      {items.map((event) => (
        <EventCard
          key={event.slug}
          slug={event.slug}
          image={event.image}
          imageAlt={t(eventKey(event, 'alt'))}
          title={t(eventKey(event, 'title'))}
          location={t(eventKey(event, 'location'))}
          date={event.date}
        />
      ))}
    </div>
  )
}

/* ============================================================
   Main component
   ============================================================ */
export default function EventsPage() {
  const { t, i18n } = useTranslation()
  const currentLang = (i18n.language || 'it') as SupportedLanguage

  usePageSEO({
    title: t('meta.events.title'),
    description: t('meta.events.description'),
    ogTitle: t('meta.events.ogTitle'),
    ogDescription: t('meta.events.ogDescription'),
    ogImage: '/images/events/mecspe-2023.jpg',
  })

  const upcoming = useMemo(() => getUpcomingEvents(), [])
  const past = useMemo(() => getPastEvents(), [])
  const total = upcoming.length + past.length

  const upcomingCountLabel =
    upcoming.length === 1
      ? t('events.countLabelSingular')
      : t('events.countLabel')
  const pastCountLabel =
    past.length === 1 ? t('events.countLabelSingular') : t('events.countLabel')

  return (
    <main id="main-content" className="bg-ink-100">
      <PageHeader
        title={t('events.title')}
        subtitle={t('events.intro')}
        eyebrow={t('events.eyebrow')}
      />

      {/* ============================================================
          Upcoming events — highlighted, engineered layout
          ============================================================ */}
      {upcoming.length > 0 && (
        <Section tone="muted" spacing="lg">
          <header className="mb-10 md:mb-12 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="mb-3 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">
                <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
                {t('events.upcomingEyebrow')}
              </p>
              <h2 className="fx-text-balance font-bold leading-tight text-ink-900 text-2xl md:text-3xl flex items-center gap-3">
                <CalendarClock
                  className="h-6 w-6 text-brand-500 shrink-0"
                  strokeWidth={2.25}
                  aria-hidden="true"
                />
                {t('events.upcomingTitle')}
              </h2>
              <p className="mt-3 text-base leading-relaxed text-ink-600 fx-text-pretty">
                {t('events.upcomingLead')}
              </p>
            </div>
            <CountPill count={upcoming.length} label={upcomingCountLabel} />
          </header>

          <EventsGrid items={upcoming} currentLang={currentLang} />
        </Section>
      )}

      {/* ============================================================
          Past events — archive tone
          ============================================================ */}
      {past.length > 0 && (
        <Section tone={upcoming.length > 0 ? 'default' : 'muted'} spacing="lg">
          <header className="mb-10 md:mb-12 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="mb-3 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">
                <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
                {t('events.pastEyebrow')}
              </p>
              <h2 className="fx-text-balance font-bold leading-tight text-ink-900 text-2xl md:text-3xl flex items-center gap-3">
                <History
                  className="h-6 w-6 text-brand-500 shrink-0"
                  strokeWidth={2.25}
                  aria-hidden="true"
                />
                {t('events.pastTitle')}
              </h2>
              <p className="mt-3 text-base leading-relaxed text-ink-600 fx-text-pretty">
                {t('events.pastLead')}
              </p>
            </div>
            <CountPill count={past.length} label={pastCountLabel} />
          </header>

          <EventsGrid items={past} currentLang={currentLang} />
        </Section>
      )}

      {/* Empty state — defensive, should not trigger with the current dataset */}
      {total === 0 && (
        <Section tone="muted" spacing="lg">
          <div className="mx-auto max-w-xl text-center">
            <p className="text-base md:text-lg text-ink-600">
              {t('events.empty')}
            </p>
            <LocalizedLink
              to="home"
              className={cn(
                'mt-6 inline-flex items-center gap-2 rounded-sm border-2 border-brand-600 bg-white px-6 py-3',
                'text-sm font-bold uppercase tracking-[0.1em] text-brand-700',
                'hover:bg-brand-600 hover:text-white transition-colors'
              )}
            >
              {t('buttons.backToHome')}
              <ArrowUpRight className="h-4 w-4" strokeWidth={2.75} />
            </LocalizedLink>
          </div>
        </Section>
      )}
    </main>
  )
}
